import {
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
  ColumnFiltersState,
  getSortedRowModel,
  getExpandedRowModel,
  ColumnPinningState,
  VisibilityState,
} from "@tanstack/react-table";
import {
  cn,
  DataTable,
  ScrollArea,
  ScrollBar,
  SearchInput,
  V2Loader,
} from "web-design-system";
import {
  usePortfolioSummaryV2,
  useSchemeFolioHoldingsData,
} from "creso-core/hooks";
import { getRouteApi, Link, useNavigate } from "@tanstack/react-router";
import { Route } from "@/routes/(auth)/_headed/clients/$id";
import { useCallback, useEffect, useMemo, useState } from "react";
import { EditFieldsPopover } from "@core/components/table/edit-fields-popover";
import { useDebounceValue, useLocalStorage } from "usehooks-ts";
import {
  createHoldingsColumns,
  NoInternalHoldings,
  PortfolioSummaryComponent,
} from "web-core";
import { DownloadReports } from "./reports/download-reports";
import { HoldingActions } from "./holdings-actions";
import { EmptyExternalPortfolio } from "./no-external-holdings";

export function HoldingsComponent() {
  const route = getRouteApi("/(auth)/_headed/clients/$id");
  const { client } = route.useLoaderData();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounceValue(search, 500)[0];
  const {
    data: holdingsData,
    isError,
    isLoading,
  } = useSchemeFolioHoldingsData(
    {
      investmentAccountGuid: client?.investment_account ?? "",
      search: debouncedSearch,
    },
    {
      enabled: !!client?.investment_account,
    }
  );

  const portfolioRoute = getRouteApi("/(auth)/_headed/clients/$id/portfolio");
  const { portfolioSummary: initialData } = portfolioRoute.useLoaderData();

  const { data: portfolioSummary } = usePortfolioSummaryV2(
    client.investment_account ?? "",
    !!client.investment_account,
    initialData
  );

  const cresoPortfolioSummary = portfolioSummary?.find(
    (p) => p.source_type === "INTERNAL"
  );

  const externalPorfolioSummary = portfolioSummary?.find(
    (p) => p.source_type === "EXTERNAL"
  );

  const hasExternalSummary =
    Number(externalPorfolioSummary?.invested_value) > 0;

  const showOverallSummary =
    hasExternalSummary && Number(cresoPortfolioSummary?.invested_value) > 0;

  const {
    source = showOverallSummary
      ? "OVERALL"
      : hasExternalSummary
        ? "EXTERNAL"
        : "INTERNAL",
  } = Route.useSearch();

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(() => {
    if (source === "OVERALL") {
      return [];
    }
    return [
      {
        id: "source",
        value: source === "INTERNAL",
      },
    ];
  });

  const columns = useMemo(
    () =>
      createHoldingsColumns({
        renderSchemeLink: ({ fundId, children }) => (
          <Link
            to="/funds/$fund_id"
            params={{ fund_id: fundId }}
            className="text-sm"
          >
            {children}
          </Link>
        ),
        renderActions: (holding) => (
          <HoldingActions holding={holding} client={client} />
        ),
      }),
    [client]
  );

  const [columnPinning] = useState<ColumnPinningState>({
    left: ["scheme_name"],
    right: ["action"],
  });

  const defaultColumnVisibility: VisibilityState = useMemo(
    () => ({
      scheme_name: true,
      folio_number: true,
      source: true,
      invested_value: true,
      absolute_return_pct: true,
      previous_day_return_pct: true,
      current_value: true,
      xirr: true,
      units: false,
      stcg: false,
      ltcg: false,
      nav: false,
      isDemat: false,
      action: true,
    }),
    []
  );

  const [storedColumnVisibility, setStoredColumnVisibility] =
    useLocalStorage<VisibilityState>(
      "holdings-table-visibility",
      defaultColumnVisibility
    );

  useEffect(() => {
    setColumnFilters((prev) => {
      const rest = prev.filter((filter) => filter.id !== "source");
      if (source === "OVERALL") return rest;
      return [...rest, { id: "source", value: source === "INTERNAL" }];
    });
  }, [source]);

  const allColumnIds = useMemo(
    () => columns.map((c) => c.id as string),
    [columns]
  );

  const [storedColumnOrder, setStoredColumnOrder] = useLocalStorage(
    "holdings-table-order",
    allColumnIds
  );

  const columnOrder = useMemo(() => {
    const existingKnown = storedColumnOrder.filter((id) =>
      allColumnIds.includes(id)
    );
    const missing = allColumnIds.filter((id) => !existingKnown.includes(id));
    return [...existingKnown, ...missing];
  }, [storedColumnOrder, allColumnIds]);

  const columnVisibility: VisibilityState = useMemo(() => {
    const normalized: VisibilityState = {} as VisibilityState;
    allColumnIds.forEach((id) => {
      const key = id as keyof VisibilityState;
      const storedValue = storedColumnVisibility[key];
      const fallback = defaultColumnVisibility[key];
      normalized[key] =
        typeof storedValue === "boolean"
          ? storedValue
          : typeof fallback === "boolean"
            ? fallback
            : true;
    });
    return normalized;
  }, [allColumnIds, storedColumnVisibility, defaultColumnVisibility]);

  const table = useReactTable({
    data: holdingsData || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: false,
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getSubRows: (row) => row.folio_data,
    getRowCanExpand: (row) =>
      Boolean(row.original.folio_data && row.original.folio_data.length > 1),
    state: {
      columnFilters,
      columnPinning,
      columnVisibility,
      columnOrder,
    },
    onColumnVisibilityChange: setStoredColumnVisibility,
    onColumnOrderChange: setStoredColumnOrder,
    defaultColumn: {
      minSize: 125,
    },
  });

  const editColumnConfig = useMemo(() => {
    return columnOrder.map((id) => {
      const columnConfig = columns.find((c) => c.id === id);
      return {
        id,
        label: columnConfig?.label,
        visible: columnVisibility[id as keyof VisibilityState],
        group: columnConfig?.group ?? "All columns",
        configurable: columnConfig?.configurable ?? true,
      };
    });
  }, [columnVisibility, columns, columnOrder]);

  const onSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(e.target.value);
    },
    []
  );

  const hasSourceFilteredHoldings = useMemo(() => {
    if (source === "OVERALL") return !!holdingsData?.length;
    if (source === "INTERNAL")
      return holdingsData?.some((h) => h.internal_flag);
    return holdingsData?.some((h) => !h.internal_flag);
  }, [holdingsData, source]);

  return (
    <V2Loader isLoading={isLoading} className="flex-1 gap-4">
      {isError || (!hasSourceFilteredHoldings && !debouncedSearch) ? (
        source === "INTERNAL" ? (
          <NoInternalHoldings
            title="First Transaction"
            description="Cement the relationship, start investing."
            actionLabel="Create Transaction"
            onAction={() =>
              navigate({
                to: ".",
                search: { modal: "transaction", client_id: client.id },
              })
            }
          />
        ) : (
          <EmptyExternalPortfolio client={client} />
        )
      ) : (
        <div className="flex flex-col flex-1 gap-4">
          <PortfolioSummaryComponent
            summary={portfolioSummary?.find((f) => f.source_type === source)}
            isLoading={false}
          />
          <div className="flex flex-col flex-1 gap-4">
            <div className="flex flex-row w-full gap-2 items-center">
              <div className="w-70">
                <SearchInput
                  placeholder="Search scheme name"
                  onChange={onSearchChange}
                />
              </div>
              <div className="ml-auto items-center flex gap-2">
                <DownloadReports clientId={client?.id} />
                <EditFieldsPopover
                  className="shrink-0"
                  columnsConfig={editColumnConfig}
                  onReorder={setStoredColumnOrder}
                  onVisibilityChange={(values) =>
                    setStoredColumnVisibility(
                      (prev: VisibilityState): VisibilityState => ({
                        ...prev,
                        ...values,
                      })
                    )
                  }
                />
              </div>
            </div>
            <ScrollArea className="flex-1 flex flex-col border border-border rounded-lg">
              <DataTable
                className={cn(
                  table.getFilteredRowModel().rows.length === 0 && "flex-1",
                  "border-0 flex-1"
                )}
                pagination={false}
                table={table}
              />
              <ScrollBar orientation="horizontal" />
            </ScrollArea>
          </div>
        </div>
      )}
    </V2Loader>
  );
}
